// Trees — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/linkedList.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer (using ApproachVisualizer's tree-rendering support)
// wherever it clarifies the recursion.

const treeProblems = [
  {
    id: 'invert-binary-tree',
    title: 'Invert Binary Tree',
    difficulty: 'Easy',
    leetcode: { number: 226, url: 'https://leetcode.com/problems/invert-binary-tree/' },
    statement: 'Given the root of a binary tree, invert the tree (mirror it left-to-right at every node) and return its root.',
    example: {
      input: 'root = [4, 2, 7, 1, 3, 6, 9]',
      output: '[4, 7, 2, 9, 6, 3, 1]',
      note: 'Every node\'s left and right children are swapped, all the way down.',
    },
    approaches: [
      {
        name: 'Approach 1 — Iterative (Level-Order With a Queue)',
        why:
          "Walk the tree level by level, and at each node visited, swap its left and right children before " +
          "enqueuing them. No recursion at all — this avoids Java's call stack entirely, which matters for " +
          "very deep or unbalanced trees.",
        steps: [
          'Start a queue containing just the root.',
          'While the queue isn\'t empty, dequeue a node and swap its left and right children.',
          'Enqueue both (now-swapped) children, if they exist.',
          'Repeat until the queue is empty — every node gets its children swapped exactly once.',
        ],
        code:
`public TreeNode invertTreeIterative(TreeNode root) {
    if (root == null) return null;
    Queue<TreeNode> queue = new LinkedList<>();
    queue.add(root);
    while (!queue.isEmpty()) {
        TreeNode node = queue.poll();
        TreeNode temp = node.left;
        node.left = node.right;
        node.right = temp;
        if (node.left != null) queue.add(node.left);
        if (node.right != null) queue.add(node.right);
    }
    return root;
}`,
        complexity: { time: 'O(n)', space: 'O(w) — the queue, up to a full level\'s width' },
        whenToUse:
          "Prefer this when the tree could be very deep and unbalanced — the queue-based approach never risks " +
          "a StackOverflowError the way deep recursion could.",
      },
      {
        name: 'Approach 2 — Recursive (DFS)',
        why:
          "Inverting a tree is really just \"invert the left subtree, invert the right subtree, then swap them " +
          "onto this node\" — a direct recursive definition that mirrors the problem statement almost exactly.",
        steps: [
          'If the node is null, there\'s nothing to invert — the base case.',
          'Recursively invert the left subtree.',
          'Recursively invert the right subtree.',
          'Swap the (now-inverted) left and right subtrees onto the current node, and return it.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 4, left: { val: 2, left: { val: 1, left: null, right: null }, right: { val: 3, left: null, right: null } }, right: { val: 7, left: { val: 6, left: null, right: null }, right: { val: 9, left: null, right: null } } } },
          varNames: [],
          steps: [
            { highlights: { tree: [1] }, note: '1 and 3 are leaves — invert(1) and invert(3) just return themselves unchanged.' },
            { snapshots: { tree: { val: 4, left: { val: 2, left: { val: 3, left: null, right: null }, right: { val: 1, left: null, right: null } }, right: { val: 7, left: { val: 6, left: null, right: null }, right: { val: 9, left: null, right: null } } } }, highlights: { tree: [2] }, note: 'Back at 2: swap its (already-inverted) children — 2\'s left is now 3, right is now 1.' },
            { snapshots: { tree: { val: 4, left: { val: 2, left: { val: 3, left: null, right: null }, right: { val: 1, left: null, right: null } }, right: { val: 7, left: { val: 9, left: null, right: null }, right: { val: 6, left: null, right: null } } } }, highlights: { tree: [7] }, note: 'Same thing happens on the right: 7\'s children swap to left=9, right=6.' },
            { snapshots: { tree: { val: 4, left: { val: 7, left: { val: 9, left: null, right: null }, right: { val: 6, left: null, right: null } }, right: { val: 2, left: { val: 3, left: null, right: null }, right: { val: 1, left: null, right: null } } } }, highlights: { tree: [4] }, note: 'Finally, the root: swap 4\'s children — left becomes the (inverted) 7-subtree, right becomes the (inverted) 2-subtree. Full mirror complete.' },
          ],
        },
        code:
`public TreeNode invertTree(TreeNode node) {
    if (node == null) return null;
    TreeNode left = invertTree(node.left);
    TreeNode right = invertTree(node.right);
    node.left = right;
    node.right = left;
    return node;
}`,
        complexity: { time: 'O(n)', space: 'O(h) — call-stack depth' },
        whenToUse:
          "This is the default answer for this problem — it's short, it reads like the problem statement, and " +
          "trees in interview problems are rarely deep enough for the recursion depth to be a real concern.",
      },
    ],
  },
  {
    id: 'maximum-depth-of-binary-tree',
    title: 'Maximum Depth of Binary Tree',
    difficulty: 'Easy',
    leetcode: { number: 104, url: 'https://leetcode.com/problems/maximum-depth-of-binary-tree/' },
    statement: 'Given the root of a binary tree, return its maximum depth — the number of nodes along the longest path from the root down to the farthest leaf.',
    example: {
      input: 'root = [3, 9, 20, null, null, 15, 7]',
      output: '3',
      note: 'The path 3 → 20 → 15 (or 3 → 20 → 7) has 3 nodes.',
    },
    approaches: [
      {
        name: 'Approach 1 — Iterative (Level-Order, Count the Levels)',
        why:
          "The maximum depth is just the number of levels the tree has. Process the tree one full level at a " +
          "time with a queue, incrementing a counter once per level, regardless of how many nodes are in it.",
        steps: [
          'Start a queue with the root, and a depth counter at 0.',
          'While the queue isn\'t empty, note how many nodes are in the current level.',
          'Dequeue exactly that many nodes, enqueueing their children as you go, then increment the depth counter.',
          'Once the queue empties, the counter holds the maximum depth.',
        ],
        code:
`public int maxDepthIterative(TreeNode root) {
    if (root == null) return 0;
    Queue<TreeNode> queue = new LinkedList<>();
    queue.add(root);
    int depth = 0;
    while (!queue.isEmpty()) {
        int levelSize = queue.size();
        for (int i = 0; i < levelSize; i++) {
            TreeNode node = queue.poll();
            if (node.left != null) queue.add(node.left);
            if (node.right != null) queue.add(node.right);
        }
        depth++;
    }
    return depth;
}`,
        complexity: { time: 'O(n)', space: 'O(w) — the queue' },
        whenToUse: "A solid, easy-to-explain answer, especially if a level-by-level result is also needed elsewhere in the same problem.",
      },
      {
        name: 'Approach 2 — Recursive (Postorder)',
        why:
          "A tree's depth is 1 (for the current node) plus whichever of its two subtrees is deeper. That's a " +
          "direct recursive definition — no queue, no bookkeeping.",
        steps: [
          'If the node is null, its depth is 0 — the base case.',
          'Recursively compute the depth of the left subtree.',
          'Recursively compute the depth of the right subtree.',
          'Return 1 plus whichever of the two is bigger.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 3, left: { val: 9, left: null, right: null }, right: { val: 20, left: { val: 15, left: null, right: null }, right: { val: 7, left: null, right: null } } } },
          varNames: [],
          steps: [
            { highlights: { tree: [9] }, note: 'depth(9) = 1 (a leaf, both children null → 0).' },
            { highlights: { tree: [15] }, note: 'depth(15) = 1 (a leaf).' },
            { highlights: { tree: [7] }, note: 'depth(7) = 1 (a leaf).' },
            { highlights: { tree: [20] }, note: 'depth(20) = 1 + max(depth(15), depth(7)) = 1 + max(1,1) = 2.' },
            { highlights: { tree: [3] }, note: 'depth(3) = 1 + max(depth(9), depth(20)) = 1 + max(1, 2) = 3.' },
          ],
        },
        code:
`public int maxDepth(TreeNode node) {
    if (node == null) return 0;
    return 1 + Math.max(maxDepth(node.left), maxDepth(node.right));
}`,
        complexity: { time: 'O(n)', space: 'O(h) — call-stack depth' },
        whenToUse: "This is the standard answer — three lines, and it's the exact template every other \"compute something bottom-up\" tree problem in this pattern builds on.",
      },
    ],
  },
  {
    id: 'same-tree',
    title: 'Same Tree',
    difficulty: 'Easy',
    leetcode: { number: 100, url: 'https://leetcode.com/problems/same-tree/' },
    statement: 'Given the roots of two binary trees, determine if they are structurally identical and have the same node values at every position.',
    example: {
      input: 'p = [1, 2, 3], q = [1, 2, 3]',
      output: 'true',
      note: 'Same shape, same values at every position.',
    },
    approaches: [
      {
        name: 'Approach 1 — Serialize Both Trees, Compare Strings',
        why:
          "Convert both trees into a string using preorder traversal with explicit null markers (the same " +
          "technique from the Serialize/Deserialize drill), then simply compare the two strings for equality. " +
          "Correct, but allocates two full strings just to answer a yes/no question.",
        steps: [
          'Serialize tree p into a string, using a marker like "#" for every null child.',
          'Serialize tree q the same way.',
          'Compare the two strings directly.',
          'Equal strings mean identical structure and values; this costs extra memory for both strings.',
        ],
        code:
`public boolean isSameTreeBruteForce(TreeNode p, TreeNode q) {
    return serialize(p).equals(serialize(q));
}

private String serialize(TreeNode node) {
    if (node == null) return "#,";
    return node.val + "," + serialize(node.left) + serialize(node.right);
}`,
        complexity: { time: 'O(n)', space: 'O(n) — two full serialized strings' },
        whenToUse: "Only useful to confirm what \"same tree\" means — comparing directly, node by node, avoids building either string at all.",
      },
      {
        name: 'Approach 2 — Optimized: Direct Recursive Comparison',
        why:
          "Two trees are the same exactly when: both are null (trivially equal), or both are non-null with " +
          "equal values AND their left subtrees are the same AND their right subtrees are the same. That's a " +
          "direct recursive check with no intermediate representation needed at all.",
        steps: [
          'If both nodes are null, they match at this position — return true.',
          'If exactly one is null (or their values differ), they don\'t match — return false immediately.',
          'Otherwise, recursively check that both left subtrees match AND both right subtrees match.',
          'The comparison can short-circuit the instant any mismatch is found.',
        ],
        code:
`public boolean isSameTree(TreeNode p, TreeNode q) {
    if (p == null && q == null) return true;
    if (p == null || q == null || p.val != q.val) return false;
    return isSameTree(p.left, q.left) && isSameTree(p.right, q.right);
}`,
        complexity: { time: 'O(n)', space: 'O(h)' },
        whenToUse: "This is the default answer — it short-circuits on the very first mismatch instead of building complete representations of both trees first.",
      },
    ],
  },
  {
    id: 'subtree-of-another-tree',
    title: 'Subtree of Another Tree',
    difficulty: 'Easy',
    leetcode: { number: 572, url: 'https://leetcode.com/problems/subtree-of-another-tree/' },
    statement:
      'Given the roots of two binary trees root and subRoot, return true if there is a subtree of root with the ' +
      'same structure and node values as subRoot.',
    example: {
      input: 'root = [3, 4, 5, 1, 2], subRoot = [4, 1, 2]',
      output: 'true',
      note: 'The subtree rooted at the node with value 4 matches subRoot exactly.',
    },
    approaches: [
      {
        name: 'Approach 1 — Check "Same Tree" at Every Node',
        why:
          "For every single node in root, check whether the subtree rooted THERE is identical to subRoot (reusing " +
          "the Same Tree check). Correct and simple to reason about, but re-running a full O(n) comparison at " +
          "every one of root's nodes adds up.",
        steps: [
          'Walk every node in root.',
          'At each one, run the Same Tree check between that node\'s subtree and subRoot.',
          'The moment any node\'s subtree matches, return true.',
          'If no node ever matches, return false. Each check costs O(size of subRoot), run at every node in root.',
        ],
        code:
`public boolean isSubtreeBruteForce(TreeNode root, TreeNode subRoot) {
    if (root == null) return false;
    if (isSameTree(root, subRoot)) return true;
    return isSubtreeBruteForce(root.left, subRoot) || isSubtreeBruteForce(root.right, subRoot);
}

private boolean isSameTree(TreeNode a, TreeNode b) {
    if (a == null && b == null) return true;
    if (a == null || b == null || a.val != b.val) return false;
    return isSameTree(a.left, b.left) && isSameTree(a.right, b.right);
}`,
        complexity: { time: 'O(m · n) — m = size of root, n = size of subRoot', space: 'O(h)' },
        whenToUse: "A perfectly acceptable first answer for reasonably sized trees — mention the hashing-based optimization below as a follow-up if asked to do better.",
      },
      {
        name: 'Approach 2 — Optimized: Canonical Subtree Signatures',
        why:
          "Build a canonical string signature for every subtree of root in a single bottom-up pass (each node's " +
          "signature is built from its own value plus its already-computed children's signatures), collecting " +
          "them into a set. Then a single lookup checks whether subRoot's own signature is anywhere in that set " +
          "— turning m separate O(n) comparisons into one O(m+n) pass.",
        steps: [
          'Compute subRoot\'s own canonical signature once.',
          'Walk root bottom-up (postorder), building each node\'s signature from its value and its children\'s already-known signatures.',
          'Store every signature seen in a HashSet as they\'re computed.',
          'Check whether subRoot\'s signature is present in that set.',
        ],
        code:
`public boolean isSubtree(TreeNode root, TreeNode subRoot) {
    Set<String> signatures = new HashSet<>();
    buildSignatures(root, signatures);
    return signatures.contains(serialize(subRoot));
}

private String buildSignatures(TreeNode node, Set<String> signatures) {
    if (node == null) return "#";
    String left = buildSignatures(node.left, signatures);
    String right = buildSignatures(node.right, signatures);
    String signature = node.val + "," + left + "," + right;
    signatures.add(signature);
    return signature;
}

private String serialize(TreeNode node) {
    if (node == null) return "#";
    return node.val + "," + serialize(node.left) + "," + serialize(node.right);
}`,
        complexity: { time: 'O(m + n)', space: 'O(m + n) — the set of signatures' },
        interviewNote: "Worth naming the subtlety out loud: signatures must encode STRUCTURE, not just a sorted bag of values — otherwise two differently-shaped subtrees with the same values could collide.",
        whenToUse: "Reach for this once the brute-force cost is called out as a concern — bottom-up signature hashing is the standard way to turn repeated whole-subtree comparisons into O(1) set lookups.",
      },
    ],
  },
  {
    id: 'balanced-binary-tree',
    title: 'Balanced Binary Tree',
    difficulty: 'Easy',
    leetcode: { number: 110, url: 'https://leetcode.com/problems/balanced-binary-tree/' },
    statement:
      'Given a binary tree, determine if it is height-balanced — meaning the left and right subtrees of EVERY ' +
      'node differ in height by at most 1.',
    example: {
      input: 'root = [3, 9, 20, null, null, 15, 7]',
      output: 'true',
      note: 'Every node\'s two subtrees differ in height by at most 1.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Recompute Height at Every Node)',
        why:
          "At every node, independently compute the height of its left subtree and its right subtree (each a " +
          "full recursive walk), and check they differ by at most 1. Correct, but height gets recomputed from " +
          "scratch for every single node, redoing a huge amount of overlapping work.",
        steps: [
          'For each node, compute the height of its left subtree with a full recursive call.',
          'Compute the height of its right subtree the same way.',
          'Check the two heights differ by at most 1.',
          'Recurse to check the same condition at every other node — heights are recomputed repeatedly.',
        ],
        code:
`public boolean isBalancedBruteForce(TreeNode node) {
    if (node == null) return true;
    int leftHeight = height(node.left);
    int rightHeight = height(node.right);
    if (Math.abs(leftHeight - rightHeight) > 1) return false;
    return isBalancedBruteForce(node.left) && isBalancedBruteForce(node.right);
}

private int height(TreeNode node) {
    if (node == null) return 0;
    return 1 + Math.max(height(node.left), height(node.right));
}`,
        complexity: { time: 'O(n²)', space: 'O(h)' },
        whenToUse: "Only useful to confirm the balance condition out loud — the repeated height recomputation is exactly the wasted work the bottom-up version eliminates.",
      },
      {
        name: 'Approach 2 — Optimized: Compute Height and Check Balance in One Pass',
        why:
          "Compute height bottom-up (postorder) exactly once, and check the balance condition at the SAME time " +
          "each height is computed — no separate height() calls needed. The moment any subtree is found " +
          "unbalanced, a sentinel value (−1) propagates straight up and short-circuits the rest of the check.",
        steps: [
          'Recursively compute the height of the left and right subtrees in one combined function.',
          'If either subtree already reported unbalanced (height −1), propagate that failure immediately.',
          'Otherwise, check whether this node\'s two heights differ by more than 1 — if so, return −1.',
          'If balanced so far, return this node\'s real height (1 + the bigger of the two).',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 3, left: { val: 9, left: null, right: null }, right: { val: 20, left: { val: 15, left: null, right: null }, right: { val: 7, left: null, right: null } } } },
          varNames: [],
          steps: [
            { highlights: { tree: [9] }, note: 'height(9) = 1 (leaf), and trivially balanced.' },
            { highlights: { tree: [15] }, note: 'height(15) = 1 (leaf), balanced.' },
            { highlights: { tree: [7] }, note: 'height(7) = 1 (leaf), balanced.' },
            { highlights: { tree: [20] }, note: 'At 20: leftHeight=1, rightHeight=1. |1−1|=0 ≤ 1 — balanced here. height(20) = 2.' },
            { highlights: { tree: [3] }, note: 'At 3: leftHeight=1 (from 9), rightHeight=2 (from 20). |1−2|=1 ≤ 1 — still balanced. Every node checked exactly once: the tree is balanced.' },
          ],
        },
        code:
`public boolean isBalanced(TreeNode root) {
    return checkHeight(root) != -1;
}

private int checkHeight(TreeNode node) {
    if (node == null) return 0;
    int leftHeight = checkHeight(node.left);
    if (leftHeight == -1) return -1;   // already unbalanced somewhere below
    int rightHeight = checkHeight(node.right);
    if (rightHeight == -1) return -1;
    if (Math.abs(leftHeight - rightHeight) > 1) return -1;
    return 1 + Math.max(leftHeight, rightHeight);
}`,
        complexity: { time: 'O(n)', space: 'O(h)' },
        interviewNote: "Using −1 as a sentinel to mean \"unbalanced\" instead of a separate boolean is the trick worth explaining — it lets one return value carry both the height AND the balance verdict simultaneously.",
        whenToUse: "This single-pass, sentinel-propagating technique generalizes to any \"compute a bottom-up value, but bail out early on some failure condition\" tree problem.",
      },
    ],
  },
  {
    id: 'diameter-of-binary-tree',
    title: 'Diameter of Binary Tree',
    difficulty: 'Easy',
    leetcode: { number: 543, url: 'https://leetcode.com/problems/diameter-of-binary-tree/' },
    statement:
      'Given the root of a binary tree, return the length (in edges) of the longest path between any two nodes. ' +
      'This path may or may not pass through the root.',
    example: {
      input: 'root = [1, 2, 3, 4, 5]',
      output: '3',
      note: 'The longest path is 4 → 2 → 1 → 3 (or 5 → 2 → 1 → 3), which has 3 edges.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Height Through Every Node)',
        why:
          "For every node, the longest path THROUGH it equals its left subtree's height plus its right " +
          "subtree's height. Check every node as a candidate \"peak,\" computing height independently each time. " +
          "Correct, but height gets recomputed repeatedly, just like the naive Balanced Binary Tree approach.",
        steps: [
          'For each node, compute the height of its left and right subtrees, each with a fresh recursive call.',
          'The candidate diameter through this node is leftHeight + rightHeight.',
          'Track the maximum candidate seen across every node.',
          'Recompute height from scratch at every node — O(n) work per node, O(n²) total.',
        ],
        code:
`private int maxDiameter = 0;

public int diameterOfBinaryTreeBruteForce(TreeNode root) {
    maxDiameter = 0;
    computeDiameters(root);
    return maxDiameter;
}

private void computeDiameters(TreeNode node) {
    if (node == null) return;
    int leftHeight = height(node.left);
    int rightHeight = height(node.right);
    maxDiameter = Math.max(maxDiameter, leftHeight + rightHeight);
    computeDiameters(node.left);
    computeDiameters(node.right);
}

private int height(TreeNode node) {
    if (node == null) return 0;
    return 1 + Math.max(height(node.left), height(node.right));
}`,
        complexity: { time: 'O(n²)', space: 'O(h)' },
        whenToUse: "Only useful to confirm the \"diameter through a node = left height + right height\" insight — the height recomputation is the exact waste the single-pass version removes.",
      },
      {
        name: 'Approach 2 — Optimized: Compute Height and Update Diameter in One Pass',
        why:
          "Compute height bottom-up exactly once, and at each node — as a side effect of computing its own " +
          "height — update a running \"best diameter seen so far\" using that node's own left and right heights. " +
          "No node's height is ever computed more than once.",
        steps: [
          'Recursively compute the height of the left and right subtrees.',
          'Update a running maximum diameter with leftHeight + rightHeight, evaluated at every node along the way.',
          'Return 1 + the bigger of the two heights, exactly like the standard height function.',
          'The running maximum, once the full recursion completes, is the answer.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 1, left: { val: 2, left: { val: 4, left: null, right: null }, right: { val: 5, left: null, right: null } }, right: { val: 3, left: null, right: null } } },
          varNames: ['maxDiameter'],
          steps: [
            { highlights: { tree: [4] }, vars: { maxDiameter: 0 }, note: 'height(4) = 1 (leaf). Diameter through 4: 0 + 0 = 0.' },
            { highlights: { tree: [5] }, vars: { maxDiameter: 0 }, note: 'height(5) = 1 (leaf). Diameter through 5: 0.' },
            { highlights: { tree: [2] }, vars: { maxDiameter: 2 }, note: 'At 2: leftHeight=1 (from 4), rightHeight=1 (from 5). Diameter through 2: 1+1=2 — new max. height(2) = 2.' },
            { highlights: { tree: [3] }, vars: { maxDiameter: 2 }, note: 'height(3) = 1 (leaf). Diameter through 3: 0.' },
            { highlights: { tree: [1] }, vars: { maxDiameter: 3 }, note: 'At 1 (root): leftHeight=2 (from 2), rightHeight=1 (from 3). Diameter through 1: 2+1=3 — new max! Final answer: 3.' },
          ],
        },
        code:
`private int maxDiameter = 0;

public int diameterOfBinaryTree(TreeNode root) {
    maxDiameter = 0;
    height(root);
    return maxDiameter;
}

private int height(TreeNode node) {
    if (node == null) return 0;
    int leftHeight = height(node.left);
    int rightHeight = height(node.right);
    maxDiameter = Math.max(maxDiameter, leftHeight + rightHeight);
    return 1 + Math.max(leftHeight, rightHeight);
}`,
        complexity: { time: 'O(n)', space: 'O(h)' },
        whenToUse: "This \"compute a value bottom-up, update a global answer as a side effect\" pattern is worth recognizing — it's the same shape as the optimized Balanced Binary Tree solution, applied to a different running quantity.",
      },
    ],
  },
  {
    id: 'binary-tree-level-order-traversal',
    title: 'Binary Tree Level Order Traversal',
    difficulty: 'Medium',
    leetcode: { number: 102, url: 'https://leetcode.com/problems/binary-tree-level-order-traversal/' },
    statement: 'Given the root of a binary tree, return the values of its nodes grouped level by level, from left to right.',
    example: {
      input: 'root = [3, 9, 20, null, null, 15, 7]',
      output: '[[3], [9, 20], [15, 7]]',
      note: 'Each inner list holds one full level of the tree.',
    },
    approaches: [
      {
        name: 'Approach 1 — Recursive DFS, Tracking Depth',
        why:
          "A preorder DFS that tracks the current depth can build the same level-grouped result: append the " +
          "current node's value to the list for its depth, creating a new list the first time that depth is reached.",
        steps: [
          'Recurse depth-first, carrying the current depth as a parameter.',
          'The first time a given depth is reached, start a new list for it in the result.',
          'Append the current node\'s value to that depth\'s list.',
          'Recurse into both children with depth + 1.',
        ],
        code:
`public List<List<Integer>> levelOrderDFS(TreeNode root) {
    List<List<Integer>> result = new ArrayList<>();
    dfs(root, 0, result);
    return result;
}

private void dfs(TreeNode node, int depth, List<List<Integer>> result) {
    if (node == null) return;
    if (depth == result.size()) result.add(new ArrayList<>());
    result.get(depth).add(node.val);
    dfs(node.left, depth + 1, result);
    dfs(node.right, depth + 1, result);
}`,
        complexity: { time: 'O(n)', space: 'O(h) call stack, plus O(n) for the output' },
        whenToUse: "A neat alternative when recursion is preferred, or when the tree is deep but narrow — the call-stack cost is bounded by height rather than width.",
      },
      {
        name: 'Approach 2 — Iterative BFS (the Natural Fit)',
        why:
          "Level order IS breadth-first search — process the queue one full level at a time (using the queue's " +
          "current size as a boundary), collecting each level into its own list before moving to the next.",
        steps: [
          'Start a queue with just the root.',
          'While the queue isn\'t empty, note the current queue size — that\'s exactly this level\'s node count.',
          'Dequeue that many nodes, collecting their values into a new list and enqueueing their children.',
          'Add that level\'s list to the result, and repeat for the next level.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 3, left: { val: 9, left: null, right: null }, right: { val: 20, left: { val: 15, left: null, right: null }, right: { val: 7, left: null, right: null } } } },
          varNames: [],
          steps: [
            { highlights: { tree: [3] }, note: 'Level 0, size 1: dequeue 3, enqueue its children (9, 20). Level result: [3].' },
            { highlights: { tree: [9, 20] }, note: 'Level 1, size 2: dequeue 9 then 20, enqueueing 20\'s children (15, 7). Level result: [9, 20].' },
            { highlights: { tree: [15, 7] }, note: 'Level 2, size 2: dequeue 15 then 7 — leaves, nothing to enqueue. Level result: [15, 7]. Final: [[3], [9, 20], [15, 7]].' },
          ],
        },
        code:
`public List<List<Integer>> levelOrder(TreeNode root) {
    List<List<Integer>> result = new ArrayList<>();
    if (root == null) return result;

    Queue<TreeNode> queue = new LinkedList<>();
    queue.add(root);
    while (!queue.isEmpty()) {
        int levelSize = queue.size();
        List<Integer> level = new ArrayList<>();
        for (int i = 0; i < levelSize; i++) {
            TreeNode node = queue.poll();
            level.add(node.val);
            if (node.left != null) queue.add(node.left);
            if (node.right != null) queue.add(node.right);
        }
        result.add(level);
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(w) — the queue, up to a full level\'s width' },
        interviewNote: "Try the \"Traversal Types\" mode (Level-Order) in this pattern's interactive playground to run this exact algorithm on any tree you build.",
        whenToUse: "This is the default, most-recognized answer — the queue-with-levelSize technique is the standard building block for practically every other BFS-on-a-tree variant (zigzag order, right side view, and more).",
      },
    ],
  },
  {
    id: 'validate-binary-search-tree',
    title: 'Validate Binary Search Tree',
    difficulty: 'Medium',
    leetcode: { number: 98, url: 'https://leetcode.com/problems/validate-binary-search-tree/' },
    statement:
      'Given the root of a binary tree, determine if it is a valid binary search tree — every node\'s left ' +
      'subtree contains only values less than the node, and its right subtree contains only values greater.',
    example: {
      input: 'root = [5, 4, 6, null, null, 3, 7]',
      output: 'false',
      note: 'Node 3 sits in the right subtree of 5 (so it must be > 5), but 3 < 5 — even though 3 < 6 looks locally fine at its direct parent.',
    },
    approaches: [
      {
        name: 'Approach 1 — Inorder Traversal, Check Strictly Increasing',
        why:
          "Inorder traversal visits a valid BST's values in strictly increasing order — so running an inorder " +
          "traversal and checking the resulting sequence never decreases (and has no duplicates) is a direct, " +
          "correct test, though it needs to store the sequence to check it.",
        steps: [
          'Run a standard inorder traversal, collecting every value into a list.',
          'Walk through the list, comparing each value to the one before it.',
          'If any value isn\'t strictly greater than the previous one, the tree is invalid.',
          'If the whole list is strictly increasing, the tree is a valid BST.',
        ],
        code:
`public boolean isValidBSTBruteForce(TreeNode root) {
    List<Integer> values = new ArrayList<>();
    inorder(root, values);
    for (int i = 1; i < values.size(); i++) {
        if (values.get(i) <= values.get(i - 1)) return false;
    }
    return true;
}

private void inorder(TreeNode node, List<Integer> values) {
    if (node == null) return;
    inorder(node.left, values);
    values.add(node.val);
    inorder(node.right, values);
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the list of all values' },
        whenToUse: "A perfectly correct and easy-to-explain answer — mention the O(h)-space bounds version below if asked to trim the extra memory.",
      },
      {
        name: 'Approach 2 — Optimized: Min/Max Bounds Threaded Through Recursion',
        why:
          "Instead of checking only a node against its direct children, thread a valid (min, max) range through " +
          "the recursion — every node must fall strictly within the range established by ALL of its ancestors, " +
          "not just its immediate parent. This is exactly why 3 in the example is caught: it inherits a min " +
          "bound of 5 from being in 5's right subtree, which its direct parent 6 alone wouldn't reveal.",
        steps: [
          'Start the root with an unbounded range (−∞, +∞).',
          'At each node, check its value falls strictly inside the current (min, max) range.',
          'Recurse left with an updated range (min, node.val) — everything left must stay below this node.',
          'Recurse right with (node.val, max) — everything right must stay above this node.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 5, left: { val: 4, left: null, right: null }, right: { val: 6, left: { val: 3, left: null, right: null }, right: { val: 7, left: null, right: null } } } },
          varNames: ['min', 'max'],
          steps: [
            { highlights: { tree: [5] }, vars: { min: '-∞', max: '+∞' }, note: 'Root 5: range (−∞, +∞) — valid. Recurse left with (−∞, 5), right with (5, +∞).' },
            { highlights: { tree: [4] }, vars: { min: '-∞', max: 5 }, note: '4 must be in (−∞, 5): 4 < 5 — valid.' },
            { highlights: { tree: [6] }, vars: { min: 5, max: '+∞' }, note: '6 must be in (5, +∞): 6 > 5 — valid so far. Recurse left with (5, 6), right with (6, +∞).' },
            { highlights: { tree: [3] }, vars: { min: 5, max: 6 }, note: '3 must be in (5, 6) — the bound inherited from being in 5\'s right subtree AND 6\'s left subtree. 3 is NOT > 5 — invalid! Return false immediately.' },
          ],
        },
        code:
`public boolean isValidBST(TreeNode root) {
    return validate(root, Long.MIN_VALUE, Long.MAX_VALUE);
}

private boolean validate(TreeNode node, long min, long max) {
    if (node == null) return true;
    if (node.val <= min || node.val >= max) return false;
    return validate(node.left, min, node.val) && validate(node.right, node.val, max);
}`,
        complexity: { time: 'O(n)', space: 'O(h) — no extra list needed' },
        whenToUse: "This bounds-threading technique is the correct general answer any time a property needs validating against ALL ancestors, not just a direct parent — and it avoids the extra O(n) list entirely.",
      },
    ],
  },
  {
    id: 'lowest-common-ancestor-of-a-bst',
    title: 'Lowest Common Ancestor of a BST',
    difficulty: 'Medium',
    leetcode: { number: 235, url: 'https://leetcode.com/problems/lowest-common-ancestor-of-a-binary-search-tree/' },
    statement:
      'Given a binary search tree and two of its nodes p and q, find their lowest common ancestor — the deepest ' +
      'node that has both p and q as descendants (a node can be its own descendant).',
    example: {
      input: 'root = [6, 2, 8, 0, 4, 7, 9, null, null, 3, 5], p = 0, q = 5',
      output: '2',
      note: 'Node 2 is the deepest node with both 0 and 5 somewhere beneath it.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Store Both Root-to-Node Paths)',
        why:
          "Find the full path from the root down to p, and separately the full path down to q (each guided by " +
          "the BST property). The lowest common ancestor is the last node the two paths still agree on before " +
          "they diverge.",
        steps: [
          'Walk from the root to p, guided by the BST property, recording every node visited.',
          'Do the same for q, recording its own path.',
          'Walk both recorded paths together from the start, as long as they match.',
          'The last node where the two paths still agree is the lowest common ancestor.',
        ],
        code:
`public TreeNode lowestCommonAncestorBruteForce(TreeNode root, TreeNode p, TreeNode q) {
    List<TreeNode> pathToP = new ArrayList<>();
    List<TreeNode> pathToQ = new ArrayList<>();
    buildPath(root, p.val, pathToP);
    buildPath(root, q.val, pathToQ);

    TreeNode lca = root;
    for (int i = 0; i < Math.min(pathToP.size(), pathToQ.size()); i++) {
        if (pathToP.get(i) != pathToQ.get(i)) break;
        lca = pathToP.get(i);
    }
    return lca;
}

private void buildPath(TreeNode node, int target, List<TreeNode> path) {
    while (node != null) {
        path.add(node);
        if (target == node.val) return;
        node = (target < node.val) ? node.left : node.right;
    }
}`,
        complexity: { time: 'O(h)', space: 'O(h) — storing both full paths' },
        whenToUse: "Correct, and a fine first answer — the optimized version below reaches the same result without storing either path.",
      },
      {
        name: 'Approach 2 — Optimized: Single Pass, No Path Storage',
        why:
          "Walk down from the root exactly once. As long as both p and q are on the SAME side of the current " +
          "node (both smaller, or both bigger), the split hasn't happened yet — keep walking that direction. " +
          "The instant they're on different sides (or one of them IS the current node), the current node is " +
          "the split point — the lowest common ancestor.",
        steps: [
          'Start at the root.',
          'If both p and q are smaller than the current node, move left.',
          'If both are bigger, move right.',
          'Otherwise (they diverge, or the current node equals one of them), the current node is the answer.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 6, left: { val: 2, left: { val: 0, left: null, right: null }, right: { val: 4, left: { val: 3, left: null, right: null }, right: { val: 5, left: null, right: null } } }, right: { val: 8, left: { val: 7, left: null, right: null }, right: { val: 9, left: null, right: null } } } },
          varNames: [],
          steps: [
            { highlights: { tree: [6] }, note: 'At 6: p=0 and q=5 are both < 6 — both on the left side. Move left to 2.' },
            { highlights: { tree: [2] }, note: 'At 2: p=0 < 2, but q=5 > 2 — they diverge here! 2 is the lowest common ancestor.' },
          ],
        },
        code:
`public TreeNode lowestCommonAncestor(TreeNode root, TreeNode p, TreeNode q) {
    TreeNode node = root;
    while (node != null) {
        if (p.val < node.val && q.val < node.val) {
            node = node.left;
        } else if (p.val > node.val && q.val > node.val) {
            node = node.right;
        } else {
            return node;   // split point (or one of them IS this node)
        }
    }
    return null;
}`,
        complexity: { time: 'O(h)', space: 'O(1)' },
        whenToUse: "This is the answer to give — it's a direct application of the BST property (same halving idea as BST search) and needs no extra memory at all.",
      },
    ],
  },
  {
    id: 'kth-smallest-element-in-a-bst',
    title: 'Kth Smallest Element in a BST',
    difficulty: 'Medium',
    leetcode: { number: 230, url: 'https://leetcode.com/problems/kth-smallest-element-in-a-bst/' },
    statement: 'Given the root of a binary search tree and an integer k, return the kth smallest value in the tree (1-indexed).',
    example: {
      input: 'root = [3, 1, 4, null, 2], k = 1',
      output: '1',
      note: 'Inorder traversal visits [1, 2, 3, 4] — the smallest (k=1) is 1.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Full Inorder, Then Index In)',
        why:
          "Inorder traversal visits a BST's values in sorted order. Collect the ENTIRE sequence into a list, " +
          "then read off the element at position k − 1. Correct, but visits every node even when k is small " +
          "and the answer is found early.",
        steps: [
          'Run a full inorder traversal, collecting every value into a list.',
          'Return the element at index k − 1 in that list.',
          'Even if k is 1, the traversal still visits every node in the tree before answering.',
        ],
        code:
`public int kthSmallestBruteForce(TreeNode root, int k) {
    List<Integer> values = new ArrayList<>();
    inorder(root, values);
    return values.get(k - 1);
}

private void inorder(TreeNode node, List<Integer> values) {
    if (node == null) return;
    inorder(node.left, values);
    values.add(node.val);
    inorder(node.right, values);
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the full list of values' },
        whenToUse: "Fine when k is expected to be close to n anyway — otherwise the early-stopping version below avoids the wasted traversal.",
      },
      {
        name: 'Approach 2 — Optimized: Iterative Inorder, Stop Early',
        why:
          "Use an explicit stack to perform inorder traversal iteratively, counting nodes as they're visited — " +
          "and stop the INSTANT the count reaches k, without ever building a full list or visiting nodes beyond " +
          "the answer.",
        steps: [
          'Use a stack to simulate inorder traversal: push left children as far as possible.',
          'Pop a node — that\'s the next value in sorted order — and increment a counter.',
          'If the counter reaches k, return this node\'s value immediately.',
          'Otherwise, move to the node\'s right child and repeat.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: 3, left: { val: 1, left: null, right: { val: 2, left: null, right: null } }, right: { val: 4, left: null, right: null } } },
          varNames: ['count'],
          steps: [
            { highlights: { tree: [1] }, vars: { count: 1 }, note: 'Push left as far as possible from 3: push 3, push 1 (1 has no left child). Pop 1 — count=1. k=1 reached — return 1 immediately, without ever visiting 2, 3, or 4.' },
          ],
        },
        code:
`public int kthSmallest(TreeNode root, int k) {
    Deque<TreeNode> stack = new ArrayDeque<>();
    TreeNode curr = root;
    int count = 0;

    while (curr != null || !stack.isEmpty()) {
        while (curr != null) {
            stack.push(curr);
            curr = curr.left;
        }
        curr = stack.pop();
        count++;
        if (count == k) return curr.val;
        curr = curr.right;
    }
    throw new IllegalArgumentException("k is larger than the number of nodes");
}`,
        complexity: { time: 'O(h + k)', space: 'O(h) — the stack, not the whole tree' },
        whenToUse: "This is the answer to reach for whenever only a PREFIX of a traversal is actually needed — no reason to pay for visiting the rest of the tree.",
      },
    ],
  },
  {
    id: 'binary-tree-maximum-path-sum',
    title: 'Binary Tree Maximum Path Sum',
    difficulty: 'Hard',
    leetcode: { number: 124, url: 'https://leetcode.com/problems/binary-tree-maximum-path-sum/' },
    statement:
      'Given the root of a binary tree, find the maximum possible sum of any non-empty path — a path can start ' +
      'and end at any two nodes, and doesn\'t need to pass through the root.',
    example: {
      input: 'root = [-10, 9, 20, null, null, 15, 7]',
      output: '42',
      note: 'The path 15 → 20 → 7 sums to 42, and doesn\'t use the root at all.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Every Node as a Candidate Peak)',
        why:
          "For every node, treat it as the \"peak\" of a path and separately compute the best downward-only sum " +
          "achievable from its left child and right child (each a full recursive helper call). Correct, but " +
          "computing \"best downward sum from X\" from scratch at every candidate peak redoes a huge amount of work.",
        steps: [
          'For every node, treat it as the top of a candidate path.',
          'Compute the best downward path sum starting from its left child (a fresh recursive call).',
          'Compute the same for its right child.',
          'The candidate for this peak is node.val + max(0, leftBest) + max(0, rightBest) — track the best across every node.',
        ],
        code:
`private int maxSum = Integer.MIN_VALUE;

public int maxPathSumBruteForce(TreeNode root) {
    maxSum = Integer.MIN_VALUE;
    considerEveryPeak(root);
    return maxSum;
}

private void considerEveryPeak(TreeNode node) {
    if (node == null) return;
    int leftBest = bestDownwardSum(node.left);
    int rightBest = bestDownwardSum(node.right);
    maxSum = Math.max(maxSum, node.val + Math.max(0, leftBest) + Math.max(0, rightBest));
    considerEveryPeak(node.left);
    considerEveryPeak(node.right);
}

private int bestDownwardSum(TreeNode node) {
    if (node == null) return 0;
    return node.val + Math.max(0, Math.max(bestDownwardSum(node.left), bestDownwardSum(node.right)));
}`,
        complexity: { time: 'O(n²)', space: 'O(h)' },
        whenToUse: "Only useful to establish the \"peak = node + best-of-left + best-of-right, clamped at 0\" formula out loud — the repeated bestDownwardSum calls are exactly what the single-pass version eliminates.",
      },
      {
        name: 'Approach 2 — Optimized: One Postorder Pass',
        why:
          "Each recursive call can do double duty: RETURN the best downward-only extension from this node (for " +
          "its parent to use), while also UPDATING a global running maximum that considers this node as a peak " +
          "using BOTH children at once. Every node's best-downward-sum is computed exactly once.",
        steps: [
          'Recursively compute the best downward sum from the left child, clamped at 0 (a negative contribution is worse than not extending the path at all).',
          'Do the same for the right child.',
          'Update the global maximum using node.val + leftGain + rightGain — this node acting as a peak using both sides.',
          'Return node.val + the BETTER of the two gains (not both) — a path extending upward through this node can only use one side.',
        ],
        visual: {
          tree: { label: 'tree', value: { val: -10, left: { val: 9, left: null, right: null }, right: { val: 20, left: { val: 15, left: null, right: null }, right: { val: 7, left: null, right: null } } } },
          varNames: ['globalMax'],
          steps: [
            { highlights: { tree: [9] }, vars: { globalMax: -2147483648 }, note: '9 is a leaf: gain=9. Peak-through-9 = 9 (no children to add). globalMax updates to 9.' },
            { highlights: { tree: [15] }, vars: { globalMax: 15 }, note: '15 is a leaf: gain=15. globalMax updates to 15.' },
            { highlights: { tree: [7] }, vars: { globalMax: 15 }, note: '7 is a leaf: gain=7. Not better than 15.' },
            { highlights: { tree: [20] }, vars: { globalMax: 42 }, note: 'At 20: leftGain=15, rightGain=7 (both positive, keep both). Peak-through-20 = 20+15+7 = 42 — new max! Returned gain upward: 20 + max(15,7) = 35.' },
            { highlights: { tree: [-10] }, vars: { globalMax: 42 }, note: 'At −10: leftGain=9, rightGain=35 (clamped at 0 if negative, but both positive here). Peak-through-root = −10+9+35 = 34 — not better than 42. Final answer: 42.' },
          ],
        },
        code:
`private int maxSum = Integer.MIN_VALUE;

public int maxPathSum(TreeNode root) {
    maxSum = Integer.MIN_VALUE;
    maxGain(root);
    return maxSum;
}

private int maxGain(TreeNode node) {
    if (node == null) return 0;
    int leftGain = Math.max(maxGain(node.left), 0);    // ignore negative contributions
    int rightGain = Math.max(maxGain(node.right), 0);

    maxSum = Math.max(maxSum, node.val + leftGain + rightGain);   // this node as a peak, using BOTH sides
    return node.val + Math.max(leftGain, rightGain);               // upward, only ONE side is usable
}`,
        complexity: { time: 'O(n)', space: 'O(h)' },
        interviewNote: "The subtlety worth stating clearly: the value RETURNED (for the parent's use) can only include one branch, since a path continuing upward can't fork — but the value used to update the GLOBAL max may use both branches, since that's this node acting as the path's single highest point.",
        whenToUse: "This dual-purpose recursion — return one thing, update a separate global answer as a side effect — is the hardest but most important template in this pattern group, and it reappears in some form in nearly every \"best path in a tree\" problem.",
      },
    ],
  },
];

export default treeProblems;
